import React, { useState, useMemo } from 'react'
import { useTheme } from '@mui/material/styles'
import { useParams } from 'react-router-dom'
import { endOfWeek, parseISO, startOfWeek } from 'date-fns'
import { DATE_PARAM_FORMAT } from '@/v2/constants'
import { safeFormat } from '@/v2/utils/convert'
import Filterbar from '../Filterbar'
import { Canvas, Loading } from '../styles'
import { Week, HourRuleWrapper } from './styles'
import {
  roundStartHour,
  roundEndHour,
  DEFAULT_START,
  DEFAULT_END,
} from './hooks/usePeriodOfTime'
import { getFirstHour, getLastHour } from './hooks/useLoadData'
import WeekDay from './WeekDay'
import HourRule from './Calendar'
import { useWeeklyCalendar } from '@/v2/hooks/calendar/useCalendar'

const Weekly = () => {
  const theme = useTheme()
  const { date } = useParams()
  const [subjectView, setSubjectView] = useState('room')
  const [typeFilter, setTypeFilter] = useState([])

  const startWeek = startOfWeek(parseISO(date))
  const endWeek = endOfWeek(parseISO(date))

  const { data, isLoading: loading, isError: error } = useWeeklyCalendar({
    from: safeFormat(startWeek, DATE_PARAM_FORMAT),
    to: safeFormat(endWeek, DATE_PARAM_FORMAT),
  })

  const formattedCalendarData = useMemo(
    () =>
      data?.map(({ date, ...rest }) => ({
        id: safeFormat(parseISO(date), 'dd/MM/yyyy'),
        ...rest,
      })),
    [data]
  )
  const days = formattedCalendarData || []

  const { defaultStartHour, defaultEndHour } = useMemo(() => {
    let min = null
    let max = null
    days.forEach(({ appointments, timeOffs, hours: clinicHours }) => {
      const start = getFirstHour(appointments, timeOffs, clinicHours)
      const end = getLastHour(appointments, timeOffs, clinicHours)
      if (start != null && (min === null || start < min)) min = start
      if (end != null && (max === null || end > max)) max = end
    })
    return {
      defaultStartHour:
        min != null
          ? roundStartHour(Math.min(min, DEFAULT_START))
          : DEFAULT_START,
      defaultEndHour:
        max != null ? roundEndHour(Math.max(max, DEFAULT_END)) : DEFAULT_END,
    }
  }, [days])

  if (loading) {
    return (
      <Canvas>
        <Filterbar
          onChangeSubject={setSubjectView}
          onChangeType={setTypeFilter}
        />
        <Loading>
          <p>Loading...</p>
        </Loading>
      </Canvas>
    )
  }

  return (
    <Canvas>
      <Filterbar
        onChangeSubject={setSubjectView}
        onChangeType={setTypeFilter}
      />
      <Week>
        <HourRuleWrapper theme={theme.palette}>
          <HourRule
            isHourRuleOnly
            view="week"
            date={date}
            startHour={defaultStartHour}
            endHour={defaultEndHour}
            columns={[{ id: 'fake', value: '0', title: '' }]}
            events={[
              { start: new Date(), end: new Date(), resourceId: 'fake' },
            ]}
            offHours={[]}
          />
        </HourRuleWrapper>

        {days.map(({ id, appointments, timeOffs, hours: clinicHours }, key) => {
          if (key === 0) return null

          const [day, month, year] = id.split('/')
          const formattedDay = `${year}-${month}-${day}`

          if (!loading && !error && key === 6 && !clinicHours.isOpen)
            return null

          return (
            <WeekDay
              key={formattedDay}
              date={formattedDay}
              hasHourRule={false}
              hasBorder={key !== 6}
              subjectView={subjectView}
              typeFilter={typeFilter}
              loading={loading}
              error={error}
              appointments={appointments}
              timeOffs={timeOffs}
              clinicHours={clinicHours}
              defaultStartHour={defaultStartHour}
              defaultEndHour={defaultEndHour}
            />
          )
        })}
      </Week>
    </Canvas>
  )
}

export default Weekly
